import axios from "axios";
import {
  Problem,
  ProblemRun,
  Screenshot,
  ScreenshotData,
  HumanLabel,
  LabelType,
  ScoreDispute,
} from "./types";

// API calls to production backend
// Use full URL in development (when running npm start), relative URL in production (Docker with nginx)
export const API_BASE_URL = process.env.NODE_ENV === 'development' ? "https://api.habitat.inc/v1" : "/api/v1";

// Helper to get auth headers
export const getAuthHeaders = () => {
  const accessCode = localStorage.getItem("accessCode");
  return accessCode ? { Authorization: `Bearer ${accessCode}` } : {};
};

interface DownloadArtifactOptions {
  onStart?: (filename: string) => void;
  onProgress?: (progress: number) => void;
}

// Cache implementation
interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
}

class APICache {
  private cache: Map<string, CacheEntry> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  private generateKey(endpoint: string, params?: any): string {
    // Normalize params: treat null, undefined, and empty object as "no params"
    if (!params || (typeof params === 'object' && Object.keys(params).length === 0)) {
      return endpoint;
    }
    return `${endpoint}:${JSON.stringify(params)}`;
  }

  get(endpoint: string, params?: any): any | null {
    const key = this.generateKey(endpoint, params);
    const entry = this.cache.get(key);

    if (!entry) return null;

    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  set(endpoint: string, params: any, data: any, ttl?: number): void {
    const key = this.generateKey(endpoint, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL,
    });

    // Clean up old entries if cache gets too large
    if (this.cache.size > 100) {
      const sortedEntries = Array.from(this.cache.entries()).sort(
        (a, b) => a[1].timestamp - b[1].timestamp,
      );

      // Remove oldest 20 entries
      for (let i = 0; i < 20; i++) {
        this.cache.delete(sortedEntries[i][0]);
      }
    }
  }

  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }

    // Remove entries matching pattern
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.cache.delete(key));
  }
}

const apiCache = new APICache();

// Export cache for manual management if needed
export const clearAPICache = () => apiCache.invalidate();
export const invalidateAPICache = (pattern: string) =>
  apiCache.invalidate(pattern);

export const fetchProblems = async (): Promise<Problem[]> => {
  const response = await axios.get(`${API_BASE_URL}/problems`, {
    headers: getAuthHeaders(),
  });
  return response.data.items || response.data;
};

export const fetchProblemRuns = async (
  problemId: string,
): Promise<ProblemRun[]> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/problems/${problemId}/runs`,
      {
        headers: getAuthHeaders(),
      },
    );
    return response.data.items || response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // No runs for this problem
      return [];
    }
    throw error;
  }
};

const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAusB9YpKu1kAAAAASUVORK5CYII=";

const transformMessagesToScreenshots = (
  problemName: string,
  runId: string,
  messages: any,
): ScreenshotData => {
  const screenshots: Screenshot[] = [];
  let screenshotIndex = 0;
  let lastScreenshotImage = "";

  if (!Array.isArray(messages)) {
    return {
      problem_name: problemName,
      run_id: runId,
      total_screenshots: 0,
      screenshots,
    };
  }

  const extractResultData = (content: any[]): {
    imageData: string | null;
    textSegments: string[];
  } => {
    let imageData: string | null = null;
    const textSegments: string[] = [];

    const walk = (items: any[]) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (!item) return;
        if (item.type === "image" && item.source?.data) {
          imageData = item.source.data;
        } else if (item.type === "text" && item.text) {
          textSegments.push(item.text);
        } else if (item.type === "output_text" && item.text) {
          textSegments.push(item.text);
        } else if (item.type === "input_text" && item.text) {
          textSegments.push(item.text);
        } else if (Array.isArray(item.content)) {
          walk(item.content);
        } else if (item.json) {
          try {
            textSegments.push(JSON.stringify(item.json));
          } catch {
            // ignore JSON stringify errors
          }
        }
      });
    };

    walk(content);
    return { imageData, textSegments };
  };

  const findToolResult = (
    startIndex: number,
    toolUseId: string,
  ): {
    imageData: string | null;
    textOutput: string;
    messageIndex: number;
    isError: boolean;
  } => {
    for (let j = startIndex; j < messages.length; j++) {
      const potential = messages[j];
      if (!potential || !Array.isArray(potential.content)) continue;

      for (const result of potential.content) {
        if (
          result?.type === "tool_result" &&
          result.tool_use_id === toolUseId
        ) {
          const { imageData, textSegments } = extractResultData(
            result.content ?? [],
          );
          return {
            imageData,
            textOutput: textSegments.map((txt) => txt.trim()).join("\n").trim(),
            messageIndex: j,
            isError: result.is_error === true,
          };
        }
      }
    }

    return {
      imageData: null,
      textOutput: "",
      messageIndex: startIndex - 1,
      isError: false,
    };
  };

  const getNextAssistantThought = (startIndex: number): string | undefined => {
    const safeStart = Math.max(0, startIndex);
    for (let j = safeStart; j < messages.length; j++) {
      const futureMsg = messages[j];
      if (futureMsg?.role === "assistant" && Array.isArray(futureMsg.content)) {
        const textParts: string[] = [];
        futureMsg.content.forEach((futureItem: any) => {
          if (futureItem.type === "text" && futureItem.text) {
            textParts.push(futureItem.text.trim());
          }
        });
        if (textParts.length > 0) {
          return textParts.join(" ").trim();
        }
      }
    }
    return undefined;
  };

  const formatToolInput = (
    toolName: string,
    toolInput: Record<string, unknown> | string,
  ): string | undefined => {
    if (!toolInput) {
      return undefined;
    }

    if (typeof toolInput === "string") {
      return toolInput;
    }

    if (
      toolName === "bash" &&
      typeof toolInput === "object" &&
      typeof (toolInput as any).command === "string"
    ) {
      return (toolInput as any).command as string;
    }

    try {
      return JSON.stringify(toolInput);
    } catch {
      return undefined;
    }
  };

  const getActionDescription = (input: Record<string, any>, toolName?: string): string => {
    switch (input.action) {
      case "screenshot":
        return "📸 Taking a screenshot";
      case "left_click":
        return "👆 Left clicking";
      case "right_click":
        return "👆 Right clicking";
      case "double_click":
        return "👆👆 Double clicking";
      case "drag":
        return "✋ Dragging";
      case "type":
        return `⌨️ Typing: "${input.text || ""}"`;
      case "scroll": {
        const direction = input.scroll_direction || input.direction || "down";
        const emoji = direction === "up" ? "⬆️" : "⬇️";
        return `${emoji} Scrolling ${direction}`;
      }
      case "key":
        return input.text ? `⌨️ Pressing: ${input.text}` : "⌨️ Key press";
      case "bash":
        return "🖥️ Running bash command";
      default:
        if (toolName && toolName !== input.action) {
          return `🎯 ${toolName}`;
        }
        return `🎯 ${input.action || "Action"}`;
    }
  };

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg || msg.role !== "assistant" || !Array.isArray(msg.content)) {
      continue;
    }

    let textBuffer: string[] = [];

    for (const item of msg.content) {
      if (item?.type === "text" && item.text) {
        textBuffer.push(item.text.trim());
        continue;
      }

      if (item?.type !== "tool_use") {
        continue;
      }

      const leadingText = textBuffer.join(" ").trim();
      textBuffer = [];

      const toolUseId: string = item.id;
      const toolName: string = item.name || "tool";
      const toolInput: Record<string, unknown> | string =
        (item.input as Record<string, unknown> | string) || {};

      const {
        imageData,
        textOutput,
        messageIndex,
        isError,
      } = findToolResult(i + 1, toolUseId);

      if (imageData) {
        lastScreenshotImage = `data:image/png;base64,${imageData}`;
      }

      const screenshotImage = lastScreenshotImage || TRANSPARENT_PIXEL;

      const normalizedToolInput =
        toolInput && typeof toolInput === "object"
          ? (toolInput as Record<string, unknown>)
          : {};

      const rawAction = (normalizedToolInput as { action?: unknown }).action;
      const computedAction =
        toolName === "computer"
          ? typeof rawAction === "string"
            ? (rawAction as string)
            : "screenshot"
          : toolName;

      const actionDetails: Screenshot['input'] = {
        ...(normalizedToolInput as Record<string, unknown>),
        action: computedAction,
      };

      const nextThought = getNextAssistantThought(messageIndex + 1);

      const thoughtChunks: string[] = [];
      if (leadingText) {
        thoughtChunks.push(leadingText);
      }
      if (nextThought) {
        thoughtChunks.push(nextThought);
      }

      const dedupedThoughtChunks = thoughtChunks
        .map((chunk) => chunk.trim())
        .filter((chunk, index, arr) => chunk.length > 0 && arr.indexOf(chunk) === index);

      const thoughtProcess =
        dedupedThoughtChunks.length > 0
          ? dedupedThoughtChunks.join("\n\n").trim()
          : undefined;

      const toolCommand = formatToolInput(toolName, toolInput)?.trim();

      let toolResultText: string | undefined = textOutput || undefined;
      if (!toolResultText && isError) {
        toolResultText = "Command failed (no output returned).";
      }

      screenshots.push({
        index: screenshotIndex++,
        action: actionDetails.action || toolName,
        description: getActionDescription(actionDetails, toolName),
        thoughtProcess,
        input: actionDetails,
        screenshot: screenshotImage,
        timestamp: null,
        toolName,
        toolInput,
        toolResultText,
        isToolError: isError,
        toolCommand,
      });
    }
  }

  return {
    problem_name: problemName,
    run_id: runId,
    total_screenshots: screenshots.length,
    screenshots,
  };
};

export const fetchScreenshots = async (
  problemName: string,
  runId: string,
  options?: { tutorialReadonly?: boolean },
): Promise<ScreenshotData> => {
  const compressedEndpoint = `/problem-runs/${runId}/messages-compressed`;
  const regularEndpoint = `/problem-runs/${runId}/messages`;
  const compressedParams: Record<string, any> = { quality: 70 };
  const regularParams: Record<string, any> = {};
  if (options?.tutorialReadonly) {
    compressedParams.tutorial_readonly = true;
    regularParams.tutorial_readonly = true;
  }

  // Check cache for compressed messages first
  const cachedCompressed = apiCache.get(compressedEndpoint, compressedParams);
  if (cachedCompressed) {
    // Transform cached messages to screenshot format
    return transformMessagesToScreenshots(problemName, runId, cachedCompressed);
  }

  // Check cache for regular messages
  const cachedRegular = apiCache.get(regularEndpoint, regularParams);
  if (cachedRegular) {
    // Transform cached messages to screenshot format
    return transformMessagesToScreenshots(problemName, runId, cachedRegular);
  }

  try {
    // Try compressed messages endpoint first with quality 70
    const response = await axios.get(`${API_BASE_URL}${compressedEndpoint}`, {
      params: compressedParams,
      headers: getAuthHeaders(),
    });

    // Cache the compressed messages
    apiCache.set(compressedEndpoint, compressedParams, response.data, 10 * 60 * 1000); // Cache for 10 minutes

    // Transform messages data to screenshot format
    const result = transformMessagesToScreenshots(
      problemName,
      runId,
      response.data,
    );
    return result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        // Compressed endpoint not found, fallback to regular messages endpoint
        try {
          const response = await axios.get(`${API_BASE_URL}${regularEndpoint}`, {
            headers: getAuthHeaders(),
            params: regularParams,
          });

          // Cache the raw messages
          apiCache.set(regularEndpoint, regularParams, response.data, 10 * 60 * 1000); // Cache for 10 minutes

          // Transform messages data to screenshot format
          const result = transformMessagesToScreenshots(
            problemName,
            runId,
            response.data,
          );
          return result;
        } catch (fallbackError) {
          if (axios.isAxiosError(fallbackError)) {
            if (fallbackError.response?.status === 404) {
              // 404 is expected when messages.json doesn't exist - return empty data
              return {
                problem_name: problemName,
                run_id: runId,
                total_screenshots: 0,
                screenshots: []
              };
            } else if (fallbackError.response?.status === 400 && fallbackError.response?.data?.detail) {
              // Check if the error message indicates the problem run is not completed
              const detail = fallbackError.response.data.detail;
              if (typeof detail === 'string' && detail.includes('Problem run is not completed')) {
                // Return a special response that indicates the problem run is not completed
                return {
                  problem_name: problemName,
                  run_id: runId,
                  total_screenshots: 0,
                  screenshots: [],
                  __problemRunNotCompleted: true,
                  __originalDetail: detail
                } as ScreenshotData & { __problemRunNotCompleted: boolean; __originalDetail: string };
              }
            }
          }
          throw fallbackError;
        }
      } else if (error.response?.status === 400 && error.response?.data?.detail) {
        // Check if the error message indicates the problem run is not completed
        const detail = error.response.data.detail;
        if (typeof detail === 'string' && detail.includes('Problem run is not completed')) {
          // Return a special response that indicates the problem run is not completed
          return {
            problem_name: problemName,
            run_id: runId,
            total_screenshots: 0,
            screenshots: [],
            __problemRunNotCompleted: true,
            __originalDetail: detail
          } as ScreenshotData & { __problemRunNotCompleted: boolean; __originalDetail: string };
        }
      }
    }
    throw error;
  }
};

export interface IncorrectLabel {
  problem_run_id: string;
  problem_name: string;
  user_label: "success" | "failed" | "bad_task";
  final_score: number;
  labeled_at: string;
  screenshot_count: number | null;
  human_label_id: string;
  review_notes?: string | null;
  user_label_notes?: string | null;
  dispute_rejection_reason?: string | null;
  all_labels: Array<{
    user_id: string;
    username: string;
    label: string;
    notes?: string | null;
  }>;
}

export interface IncorrectLabelsResponse {
  items: IncorrectLabel[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export const fetchIncorrectLabels = async (
  page: number = 0,
  limit: number = 20,
): Promise<IncorrectLabelsResponse> => {
  const endpoint = `/me/incorrect-labels?page=${page}&limit=${limit}`;

  const response = await axios.get<IncorrectLabelsResponse>(
    `${API_BASE_URL}${endpoint}`,
    { headers: getAuthHeaders() },
  );

  return response.data;
};

export interface UserStats {
  user_id: string;
  total_labels: number;
  accurate_labels: number;
  tiebreaker_labels: number;
  recent_accuracy: number | null;
  overall_accuracy: number | null;
  updated_at: string;
}

export const fetchUserStats = async (): Promise<UserStats> => {
  const endpoint = `/me/stats`;

  const response = await axios.get<UserStats>(`${API_BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
  });

  return response.data;
};

export const getVideoUrl = (problemName: string, runId: string): string => {
  // Video endpoint doesn't exist in current server, return empty string
  return "";
};

export interface User {
  id: string;
  username: string;
  created_at: string;
  has_completed_onboarding: boolean;
  permissions?: string[];
}

// Helper function to check if user only has Blender labeling permissions
export const isBlenderOnlyUser = (permissions?: string[]): boolean => {
  if (!permissions || permissions.length === 0) return false;

  // Get all software-related permissions (label_X or create_X)
  const softwarePermissions = permissions.filter(
    (p) => p.startsWith("label_") || p.startsWith("create_"),
  );

  // Check if ALL software permissions are for Blender
  return (
    softwarePermissions.length > 0 &&
    softwarePermissions.every((p) => p.includes("blender"))
  );
};

// Helper function to check if user only has Immich labeling permissions
export const isImmichOnlyUser = (permissions?: string[]): boolean => {
  if (!permissions || permissions.length === 0) return false;

  // Get all software-related permissions (label_X or create_X)
  const softwarePermissions = permissions.filter(
    (p) => p.startsWith("label_") || p.startsWith("create_"),
  );

  // Check if ALL software permissions are for Immich
  return (
    softwarePermissions.length > 0 &&
    softwarePermissions.every((p) => p.includes("immich"))
  );
};

// Helper function to check if user only has Odoo labeling permissions
export const isOdooOnlyUser = (permissions?: string[]): boolean => {
  if (!permissions || permissions.length === 0) return false;

  // Get all software-related permissions (label_X or create_X)
  const softwarePermissions = permissions.filter(
    (p) => p.startsWith("label_") || p.startsWith("create_"),
  );

  // Check if ALL software permissions are for Odoo
  return (
    softwarePermissions.length > 0 &&
    softwarePermissions.every((p) => p.includes("odoo"))
  );
};

export const loginWithCode = async (code: string): Promise<User> => {
  // Store the access code
  localStorage.setItem("accessCode", code);

  // Verify the code by getting user info
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${code}` },
    });

    // Store permissions in localStorage for easy access
    if (response.data.permissions) {
      localStorage.setItem(
        "userPermissions",
        JSON.stringify(response.data.permissions),
      );
    }

    // Add has_completed_onboarding field if not present
    return {
      ...response.data,
      has_completed_onboarding: true,
      permissions: response.data.permissions || [],
    };
  } catch (error) {
    // Clear the code if authentication fails
    localStorage.removeItem("accessCode");
    localStorage.removeItem("userPermissions");
    throw error;
  }
};

export const getUser = async (userId: string): Promise<User> => {
  const response = await axios.get(`${API_BASE_URL}/users/${userId}`, {
    headers: getAuthHeaders(),
  });
  return {
    ...response.data,
    has_completed_onboarding: true,
  };
};

export const fetchUser = async (userId: string): Promise<User> => {
  // For now, since we can't fetch other users without admin access,
  // we'll return the current user if the ID matches
  const currentUserId = localStorage.getItem("userId");
  if (userId === currentUserId) {
    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: getAuthHeaders(),
    });
    return {
      ...response.data,
      has_completed_onboarding: true,
    };
  }
  // Otherwise try to get the user (will fail for non-admins)
  return getUser(userId);
};

export const downloadFCStdFile = async (
  problemName: string,
  runId: string,
): Promise<void> => {
  try {
    // Try to download from artifacts endpoint
    const response = await axios.get(
      `${API_BASE_URL}/problem-runs/${runId}/artifacts/user-problem-model-output.FCStd`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${problemName}_${runId}.FCStd`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    alert(
      "Failed to download FCStd file. You may not have permission to access this file.",
    );
  }
};

// Fetch a single problem run by ID
export const fetchProblemRunById = async (
  runId: string
): Promise<ProblemRunWithProblem> => {
  const endpoint = `/problem-runs/${runId}`;
  const params = { include_problem: true };
  
  
  try {
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      params,
      headers: getAuthHeaders(),
    });
    
    const problemRun = response.data;
    
    return problemRun;
  } catch (error: any) {
    throw error;
  }
};

export const downloadModelOutputFile = async (
  runId: string,
  options: DownloadArtifactOptions = {},
): Promise<void> => {
  const defaultFilename = 'final_state.zip';
  options.onStart?.(defaultFilename);

  try {
    const response = await axios.get(
      `${API_BASE_URL}/problem-runs/${runId}/artifacts/final_state.zip`,
      {
        responseType: 'blob',
        headers: getAuthHeaders(),
        onDownloadProgress: (progressEvent) => {
          if (!options.onProgress) {
            return;
          }

          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            options.onProgress(percentCompleted);
          } else {
            // Provide a minimal progress tick when total size is unavailable
            options.onProgress(10);
          }
        },
      },
    );

    let filename = defaultFilename;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+?)"?$/i);
      if (match?.[1]) {
        filename = match[1].replace(/"/g, '');
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    options.onProgress?.(100);
  } catch (error) {
    console.error('Failed to download model output artifact:', error);
    throw error;
  }
};

export const downloadInputFile = async (
  runId: string,
  problemId: string,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  try {
    // First, get problem details to find the input filename
    const problemResponse = await axios.get(
      `${API_BASE_URL}/problems/${problemId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    const problem = problemResponse.data;

    // Check for start_state (newer software types) or input_file (older software types)
    // For multimedia problems, start_state is an object with directory_state and mattermost_state
    let inputFile: string;
    if (problem.spec?.start_state) {
      // Check if it's a multimedia problem (object with directory_state)
      if (typeof problem.spec.start_state === 'object' && problem.spec.start_state.directory_state) {
        inputFile = problem.spec.start_state.directory_state.name;
      } else if (typeof problem.spec.start_state === 'string') {
        inputFile = problem.spec.start_state;
      } else {
        inputFile = problem.spec.start_state;
      }
    } else {
      inputFile = problem.spec?.input_file;
    }

    if (!inputFile) {
      alert("No input file available for this problem.");
      return;
    }

    // Download the input file using the inputs endpoint with progress tracking
    const response = await axios.get(
      `${API_BASE_URL}/problem-runs/${runId}/inputs/${inputFile}`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = inputFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    alert(
      "Failed to download input file. The file may not be available for this problem.",
    );
  }
};

export const downloadStartState = async (
  problemId: string,
  runId: string,
  options: DownloadArtifactOptions = {},
): Promise<void> => {
  try {
    const problemResponse = await axios.get(
      `${API_BASE_URL}/problems/${problemId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    const problem = problemResponse.data;

    // Check for start_state (newer software types) or input_file (older software types)
    // For multimedia problems, start_state is an object with directory_state and mattermost_state
    let inputFile: string;
    if (problem.spec?.start_state) {
      // Check if it's a multimedia problem (object with directory_state)
      if (typeof problem.spec.start_state === 'object' && problem.spec.start_state.directory_state) {
        inputFile = problem.spec.start_state.directory_state.name;
      } else if (typeof problem.spec.start_state === 'string') {
        inputFile = problem.spec.start_state;
      } else {
        inputFile = problem.spec.start_state;
      }
    } else {
      inputFile = problem.spec?.input_file;
    }

    if (!inputFile) {
      alert('No input file available for this problem.');
      return;
    }

    options.onStart?.(inputFile);

    const response = await axios.get(
      `${API_BASE_URL}/problem-runs/${runId}/inputs/${inputFile}`,
      {
        responseType: 'blob',
        headers: getAuthHeaders(),
        onDownloadProgress: (progressEvent) => {
          if (options.onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            options.onProgress(percentCompleted);
          }
        },
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = inputFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    options.onProgress?.(100);
  } catch (error) {
    alert(
      'Failed to download input file. The file may not be available for this problem.',
    );
  }
};

export const downloadProblemSolution = async (
  problemId: string,
  runId: string,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  try {
    const problemResponse = await axios.get(
      `${API_BASE_URL}/problems/${problemId}`,
      {
        headers: getAuthHeaders(),
      },
    );

    const problem = problemResponse.data;

    // Handle different problem spec structures
    let solutionFilename: string;
    if (problem.spec?.end_states) {
      // Check if it's a multimedia problem (array of objects with directory_state)
      if (Array.isArray(problem.spec.end_states) && problem.spec.end_states.length > 0) {
        const firstEndState = problem.spec.end_states[0];
        if (typeof firstEndState === 'object' && firstEndState.directory_state) {
          solutionFilename = firstEndState.directory_state.name;
        } else if (typeof firstEndState === 'string') {
          solutionFilename = firstEndState;
        } else {
          throw new Error('Invalid end_state format in problem spec.');
        }
      } else {
        throw new Error('No solution file available for this problem.');
      }
    } else if (problem.spec?.solution_files) {
      solutionFilename = problem.spec.solution_files[0];
    } else {
      throw new Error('No solution file available for this problem.');
    }

    if (!solutionFilename) {
      throw new Error('No solution file available for this problem.');
    }

    const response = await axios.get(
      `${API_BASE_URL}/problem-runs/${runId}/inputs/${solutionFilename}`,
      {
        responseType: 'blob',
        headers: getAuthHeaders(),
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = solutionFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('Failed to download problem solution:', error);
    throw error;
  }
};

export const fetchUserSolutionBlob = async (
  problemId: string,
): Promise<Blob | null> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/solutions/${problemId}/download`,
      {
        responseType: 'blob',
        headers: getAuthHeaders(),
        // Allow 404 responses to be handled gracefully
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      },
    );

    if (response.status === 404) {
      return null;
    }

    return response.data as Blob;
  } catch (error: any) {
    console.error('Failed to fetch user solution blob:', error);
    throw error;
  }
};

export const downloadProblemRunVideo = async (runId: string): Promise<void> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/playgrounds/problem-runs/${runId}/video/download`,
      {
        responseType: 'blob',
        headers: getAuthHeaders(),
      },
    );

    const contentDisposition = response.headers['content-disposition'];
    let filename = `problem_run_${runId}_recording.webm`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+?)"?$/i);
      if (match?.[1]) {
        filename = match[1].replace(/"/g, '');
      }
    }

    const blob = new Blob([
      response.data,
    ], { type: response.headers['content-type'] || 'video/webm' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('Failed to download problem run video:', error);
    throw error;
  }
};

export const createLabel = async (
  problemRunId: string,
  userId: string,
  label: LabelType,
  confidence?: number,
  notes?: string,
  projectId?: string,
): Promise<HumanLabel> => {
  // Debug logging for missing project IDs
  if (!projectId) {
    console.error('WARNING: Creating label without project ID!', {
      problemRunId,
      userId,
      label,
      url: window.location.pathname,
      timestamp: new Date().toISOString()
    });
  }
  
  const url = projectId 
    ? `${API_BASE_URL}/human-labels?project_id=${projectId}`
    : `${API_BASE_URL}/human-labels`;
  
  const response = await axios.post(
    url,
    {
      problem_run_id: problemRunId,
      label,
      notes,
    },
    {
      headers: getAuthHeaders(),
    },
  );

  // Invalidate caches that might be affected by the new label
  apiCache.invalidate("first-unlabeled-run");
  apiCache.invalidate(`/problem-runs/${problemRunId}/human-labels`);

  return response.data;
};

export const fetchLabelsForRun = async (
  problemRunId: string,
  projectId?: string,
): Promise<HumanLabel[]> => {
  const endpoint = `/problem-runs/${problemRunId}/human-labels`;
  const params = projectId ? { project_id: projectId } : undefined;
  
  // Debug logging for missing project IDs
  if (!projectId && window.location.pathname.includes('/project/')) {
    console.error('WARNING: Fetching labels without project ID in project context!', {
      problemRunId,
      url: window.location.pathname,
      timestamp: new Date().toISOString()
    });
  }
  
  const cached = apiCache.get(endpoint, params);
  if (cached) return cached;

  const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
    params: params || {},
  });

  apiCache.set(endpoint, params, response.data, 60 * 1000); // Cache for 1 minute
  return response.data;
};

export const deleteLabel = async (labelId: string): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/human-labels/${labelId}`, {
    headers: getAuthHeaders(),
  });

  // Invalidate caches that might be affected by the deleted label
  apiCache.invalidate("first-unlabeled-run");
  apiCache.invalidate("/problem-runs/");
};

export const fetchRunPosition = async (
  runId: string,
): Promise<{
  current_position: number;
  total_runs: number;
  problem_name: string;
  run_index_in_problem: number;
  total_runs_in_problem: number;
}> => {
  // Position endpoint doesn't exist, return placeholder data
  return {
    current_position: 1,
    total_runs: 1,
    problem_name: "Unknown",
    run_index_in_problem: 1,
    total_runs_in_problem: 1,
  };
};

// Project interfaces
export interface Project {
  id: string;
  name: string;
  owner_id: string;
  owner_username?: string;
  due_date?: string | null;
  completed_at?: string | null;
  status?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectResponse extends Project {
  is_paid?: boolean;
  payment_per_label?: number;
  tutorial_readonly?: boolean;
}

export interface ProjectWithStatistics extends Project {
  total_problem_runs: number;
  labeled_problem_runs: number;
  remaining_problem_runs: number;
  tutorial_readonly?: boolean;
}

export interface ProblemRunWithProblem extends ProblemRun {
  problem?: {
    id: string;
    name: string;
    spec: any;
    is_public: boolean;
    software?: string;
  };
  human_labels?: HumanLabel[];
  label_platform_bad_task_reason?: string | null;
}

// Fetch user's assigned projects with statistics
export const fetchUserProjects = async (): Promise<ProjectWithStatistics[]> => {
  const endpoint = "/projects";
  const cached = apiCache.get(endpoint);
  if (cached) return cached;

  const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
  });

  apiCache.set(endpoint, null, response.data);
  return response.data;
};

export const fetchProject = async (
  projectId: string,
  options?: { tutorialReadonly?: boolean },
): Promise<ProjectResponse> => {
  const endpoint = `/projects/${projectId}`;
  const params: Record<string, any> = {};
  if (options?.tutorialReadonly) {
    params.tutorial_readonly = true;
  }

  const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
    params,
    headers: getAuthHeaders(),
  });

  return response.data;
};

export interface ProjectRunsResponseMetadata {
  total: number;
  page?: number;
  limit?: number;
  has_next?: boolean;
  has_prev?: boolean;
  tutorial_readonly?: boolean;
  [key: string]: any;
}

export interface ProjectRunsResponse {
  items: ProblemRunWithProblem[];
  metadata: ProjectRunsResponseMetadata;
}

// Fetch problem runs for a specific project
export const fetchProjectProblemRuns = async (
  projectId: string,
  page: number = 0,
  limit: number = 100,
  options?: { tutorialReadonly?: boolean },
): Promise<ProjectRunsResponse> => {
  const endpoint = `/projects/${projectId}/problem-runs`;
  const params: Record<string, any> = {
    page,
    limit,
    include_problem: true,
  };

  if (options?.tutorialReadonly) {
    params.tutorial_readonly = true;
  }

  const cached = apiCache.get(endpoint, params);
  if (cached) return cached;

  const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
    params,
    headers: getAuthHeaders(),
  });

  apiCache.set(endpoint, params, response.data);
  return response.data;
};

// Fetch the first unlabeled run in a project
export const fetchFirstUnlabeledRun = async (
  projectId: string,
): Promise<{
  problem_run: ProblemRunWithProblem | null;
  index: number | null;
}> => {
  const endpoint = `/projects/${projectId}/first-unlabeled-run`;
  const cached = apiCache.get(endpoint);
  if (cached) return cached;

  const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
  });

  apiCache.set(endpoint, null, response.data, 30 * 1000); // Cache for 30 seconds since this changes frequently
  return response.data;
};

// Fetch all problem runs for a specific project (up to maxRuns)
export const fetchAllProjectProblemRuns = async (
  projectId: string,
  maxRuns: number = 500,
): Promise<{ items: ProblemRunWithProblem[]; metadata: any }> => {
  const allRuns: ProblemRunWithProblem[] = [];
  let page = 0;
  const limit = 100; // Fetch 100 at a time
  let totalFetched = 0;
  let hasMore = true;

  while (hasMore && totalFetched < maxRuns) {
    const response = await fetchProjectProblemRuns(projectId, page, limit);
    allRuns.push(...response.items);
    totalFetched += response.items.length;

    // Check if there are more pages
    const hasNextPage = Boolean(response.metadata?.has_next);
    hasMore = hasNextPage && totalFetched < maxRuns;
    page++;
  }

  // Trim to maxRuns if we fetched more
  const finalRuns = allRuns.slice(0, maxRuns);


  return {
    items: finalRuns,
    metadata: {
      total: Math.min(allRuns.length, maxRuns),
      fetched_pages: page,
      limit: maxRuns,
    },
  };
};

// Project Queue interfaces
export interface ClaimableProject {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  total_runs: number;
}

export interface ClaimProjectResponse {
  project_id: string;
  project_name: string;
  due_date: string;
  total_runs: number;
}

// Fetch claimable projects from the queue
export const fetchClaimableProjects = async (): Promise<ClaimableProject[]> => {
  const endpoint = "/project-queue/claimable";
  const cached = apiCache.get(endpoint);
  if (cached) return cached;

  const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
  });

  apiCache.set(endpoint, null, response.data, 60 * 1000); // Cache for 1 minute
  return response.data;
};

// Claim a project from the queue
export const claimProject = async (
  projectQueueAssignmentId: string,
): Promise<ClaimProjectResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/project-queue/${projectQueueAssignmentId}/claim`,
    {},
    { headers: getAuthHeaders() },
  );

  // Invalidate caches
  apiCache.invalidate("project-queue");
  apiCache.invalidate("projects");

  return response.data;
};

// Complete a project
export const completeProject = async (projectId: string): Promise<void> => {
  await axios.post(
    `${API_BASE_URL}/projects/${projectId}/complete`,
    {},
    { headers: getAuthHeaders() },
  );

  // Invalidate caches
  apiCache.invalidate("projects");
  apiCache.invalidate(`/projects/${projectId}`);
};

// Score dispute types
export interface ScoreDisputeCreate {
  problem_run_id: string;
  note: string;
  proposed_score: "success" | "failed" | "bad_task";
}

export const createScoreDispute = async (
  disputeData: ScoreDisputeCreate,
): Promise<any> => {
  const response = await axios.post(
    `${API_BASE_URL}/score-disputes`,
    disputeData,
    { headers: getAuthHeaders() },
  );
  return response.data;
};

export const fetchProblemRunDisputes = async (
  problemRunId: string,
): Promise<ScoreDispute[]> => {
  const endpoint = `/problem-runs/${problemRunId}/score-disputes`;
  const cached = apiCache.get(endpoint, undefined);
  if (cached) return cached;

  try {
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
    });
    
    const result = response.data;
    apiCache.set(endpoint, undefined, result, 60 * 1000); // Cache for 1 minute
    return result;
  } catch (error: any) {
    // If it's a 404, return empty array (no disputes)
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

// Removed fetchDisputesForRun - use fetchDisputeForResolution instead

export interface DisputeListResponse {
  disputes: ScoreDispute[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
}

export const fetchUserDisputes = async (
  userId: string,
  page: number = 0,
  limit: number = 20,
): Promise<DisputeListResponse> => {
  const endpoint = `/users/${userId}/score-disputes`;
  const params = { page, limit };
  const cached = apiCache.get(endpoint, params);
  if (cached) return cached;

  try {
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
      params,
    });

    // The backend already includes problem_name in the response
    const result = response.data;

    apiCache.set(endpoint, params, result, 60 * 1000); // Cache for 1 minute
    return result;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAllPendingDisputes = async (
  page: number = 0,
  limit: number = 20,
  sortBy: string = 'problem_name',
  sortOrder: string = 'asc',
): Promise<DisputeListResponse> => {
  const endpoint = `/score-disputes`;
  const params = { page, limit, status: 'pending', sort_by: sortBy, sort_order: sortOrder };
  const cached = apiCache.get(endpoint, params);
  if (cached) return cached;

  try {
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
      params,
    });

    // The backend already includes problem_name in the response
    const result = response.data;

    apiCache.set(endpoint, params, result, 60 * 1000); // Cache for 1 minute
    return result;
  } catch (error: any) {
    // If it's a 403 (forbidden), return empty results instead of throwing
    if (error.response?.status === 403) {
      return {
        disputes: [],
        total: 0,
        page: page,
        limit: limit,
        has_next: false,
        has_prev: false,
      };
    }
    throw error;
  }
};

export const updateDisputeStatus = async (
  disputeId: string,
  status: 'accepted' | 'rejected' | 'pending',
  reason: string,
  finalStatus?: 'success' | 'failed' | 'bad_task',
): Promise<ScoreDispute> => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/score-disputes/${disputeId}/status`,
      { status, reason, final_status: finalStatus },
      { headers: getAuthHeaders() },
    );

    // Invalidate dispute caches
    apiCache.invalidate('/score-disputes');
    const userId = localStorage.getItem("userId");
    if (userId) {
      apiCache.invalidate(`/users/${userId}/score-disputes`);
    }

    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const deleteDispute = async (disputeId: string): Promise<void> => {
  try {
    await axios.delete(
      `${API_BASE_URL}/score-disputes/${disputeId}`,
      { headers: getAuthHeaders() },
    );

    // Invalidate dispute caches
    apiCache.invalidate('/score-disputes');
    const userId = localStorage.getItem("userId");
    if (userId) {
      apiCache.invalidate(`/users/${userId}/score-disputes`);
    }
  } catch (error: any) {
    throw error;
  }
};

export const fetchDisputeForResolution = async (
  problemRunId: string,
): Promise<ScoreDispute | null> => {
  const endpoint = `/problem-runs/${problemRunId}/dispute-for-resolution`;
  
  try {
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
    });

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // No dispute for this run
      return null;
    }
    if (error.response?.status === 403) {
      // User doesn't have permission
      return null;
    }
    throw error;
  }
};


export const fetchResolvedDisputes = async (
  page: number = 0,
  limit: number = 20,
  sortBy: string = 'problem_name',
  sortOrder: string = 'asc',
  searchQuery: string = '',
): Promise<DisputeListResponse> => {
  // Get the current user's ID
  const userId = localStorage.getItem("userId");
  if (!userId) {
    return {
      disputes: [],
      total: 0,
      page: page,
      limit: limit,
      has_next: false,
      has_prev: false,
    };
  }

  // Fetch resolved disputes (both accepted and rejected) resolved by current user
  // Use the backend's sorting capabilities
  try {
    const endpoint = `/score-disputes`;
    const params: any = {
      page,
      limit,
      resolved_by_user_id: userId,
      sort_by: sortBy,
      sort_order: sortOrder
    };

    // Only add search_query if it's not empty
    if (searchQuery && searchQuery.trim()) {
      params.search_query = searchQuery.trim();
    }

    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
      params,
    });

    // Filter to only show resolved disputes (accepted or rejected)
    const resolvedDisputes = response.data.disputes.filter(
      (d: ScoreDispute) => d.status === 'accepted' || d.status === 'rejected'
    );
    
    // The backend already includes problem_name in the response
    const result: DisputeListResponse = {
      disputes: resolvedDisputes,
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      has_next: response.data.has_next,
      has_prev: response.data.has_prev,
    };

    return result;
  } catch (error: any) {
    // If it's a 403 (forbidden), return empty results instead of throwing
    if (error.response?.status === 403) {
      return {
        disputes: [],
        total: 0,
        page: page,
        limit: limit,
        has_next: false,
        has_prev: false,
      };
    }
    throw error;
  }
};

// Conflicting disputes types and functions
export interface ConflictingDisputeInfo {
  dispute_id: string;
  user_id: string;
  username: string | null;
  proposed_score: string;
  final_status: string | null;
  note: string;
  created_at: string | null;
  resolved_at: string | null;
}

export interface ConflictingDisputeRun {
  problem_run_id: string;
  problem_id: string;
  problem_name: string;
  problem_software: string;
  resolved_by_user_id: string;
  resolved_by_username: string | null;
  disputes: ConflictingDisputeInfo[];
  distinct_final_statuses: string[];
}

export interface ConflictingDisputesResponse {
  conflicting_runs: ConflictingDisputeRun[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
}

export const fetchConflictingDisputes = async (
  page: number = 0,
  limit: number = 20,
): Promise<ConflictingDisputesResponse> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/score-disputes/conflicting-resolutions`,
      {
        headers: getAuthHeaders(),
        params: { page, limit },
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 403) {
      return {
        conflicting_runs: [],
        total: 0,
        page: page,
        limit: limit,
        has_next: false,
        has_prev: false,
      };
    }
    throw error;
  }
};

// Mistake acknowledgement functions
export const acknowledgeMistake = async (humanLabelId: string): Promise<any> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/human-labels/mistakes/acknowledge`,
      { human_label_id: humanLabelId },
      { headers: getAuthHeaders() }
    );
    
    // Invalidate relevant caches
    apiCache.invalidate('/me/incorrect-labels');
    apiCache.invalidate('/human-labels/mistakes/acknowledged-ids');
    apiCache.invalidate('/human-labels/mistakes/acknowledgments');
    
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getAcknowledgedMistakeIds = async (): Promise<string[]> => {
  const endpoint = '/human-labels/mistakes/acknowledged-ids';
  const cached = apiCache.get(endpoint);
  if (cached) return cached;

  try {
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
    });
    
    // Cache for 5 minutes
    apiCache.set(endpoint, null, response.data, 5 * 60 * 1000);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const deleteMistakeAcknowledgment = async (acknowledgmentId: string): Promise<void> => {
  try {
    await axios.delete(
      `${API_BASE_URL}/human-labels/mistakes/acknowledgments/${acknowledgmentId}`,
      { headers: getAuthHeaders() }
    );
    
    // Invalidate relevant caches
    apiCache.invalidate('/me/incorrect-labels');
    apiCache.invalidate('/human-labels/mistakes/acknowledged-ids');
    apiCache.invalidate('/human-labels/mistakes/acknowledgments');
  } catch (error: any) {
    throw error;
  }
};

export const getUserAcknowledgments = async (): Promise<any[]> => {
  const endpoint = '/human-labels/mistakes/acknowledgments';
  const cached = apiCache.get(endpoint);
  if (cached) return cached;

  try {
    const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
      headers: getAuthHeaders(),
    });
    
    // Cache for 5 minutes
    apiCache.set(endpoint, null, response.data, 5 * 60 * 1000);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Solution upload API functions
export const checkIfSolutionExists = async (
  problemId: string
): Promise<boolean> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/solutions/${problemId}`,
      { headers: getAuthHeaders() }
    );

    if (response.status !== 200 || !response.data) {
      return false;
    }

    return response.data.exists === true;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return false;
    }
    throw error;
  }
};

export const validateFilenames = async (
  description: string,
  solutionFilenames: string[]
): Promise<{ is_valid: boolean; issues: string[]; suggestion: string | null; error: string | null }> => {
  const formData = new FormData();
  formData.append('description', description);
  formData.append('output_filenames', JSON.stringify(solutionFilenames));

  const response = await axios.post(
    `${API_BASE_URL}/problems/validate-filenames`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const generateSolutionUploadUrl = async (
  problemId: string,
  filename: string,
  expirationMinutes: number = 15
): Promise<string> => {
  const response = await axios.post(
    `${API_BASE_URL}/solutions/upload-urls`,
    {
      problem_id: problemId,
      filename: filename,
      expiration_minutes: expirationMinutes,
    },
    {
      headers: getAuthHeaders(),
    }
  );
  return response.data.url;
};

export const uploadSolution = async (
  problemId: string,
  projectId: string | null | undefined,
  file: File
): Promise<void> => {
  // Validate file size (500MB limit)
  const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB in bytes
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `File size exceeds maximum limit of 500MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`
    );
  }

  // Step 1: Generate signed URL
  const uploadUrl = await generateSolutionUploadUrl(problemId, file.name);

  // Step 2: Upload file directly to GCS
  // Check if it's a signed URL (starts with http/https) or local path
  if (uploadUrl.startsWith('http')) {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });
  }
  // For local development paths, skip the upload step as files are handled differently

  // Step 3: Finalize the solution record in the backend
  const userId = localStorage.getItem('userId');
  if (!userId) {
    throw new Error('User ID not found in localStorage');
  }

  const gcsPath = `solutions/${problemId}/${userId}/${file.name}`;

  const uploadResponse = await axios.post(
    `${API_BASE_URL}/solutions/upload`,
    {
      problem_id: problemId,
      file_path: gcsPath,
      filename: file.name,
      file_size: file.size,
      content_type: file.type || 'application/octet-stream',
    },
    {
      headers: getAuthHeaders(),
    }
  );

  const solutionId = uploadResponse.data.solution_id;

  // Step 4: Trigger comparison generation for all problem runs
  if (solutionId) {
    try {
      // Fetch all problem runs for this problem
      const problemRuns = await fetchProblemRuns(problemId);

      if (problemRuns.length > 0) {
        const problemRunIds = problemRuns.map(run => run.id);

        // Trigger comparison generation (fire and forget - don't wait for completion)
        axios.post(
          `${API_BASE_URL}/solutions/${solutionId}/generate-comparisons`,
          { problem_run_ids: problemRunIds },
          { headers: getAuthHeaders() }
        ).catch(error => {
          // Log error but don't fail the upload
          console.error('Failed to trigger comparison generation:', error);
        });
      }
    } catch (error) {
      // Log error but don't fail the upload
      console.error('Failed to fetch problem runs or trigger comparisons:', error);
    }
  }

  // Invalidate solution cache
  apiCache.invalidate(`/solutions/${problemId}`);
  // Invalidate project cache since solution upload affects project completion
  if (projectId) {
    apiCache.invalidate(`/projects/${projectId}`);
  }
  apiCache.invalidate('/projects');
};

export const downloadSolution = async (
  problemId: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/solutions/${problemId}/download`,
      {
        responseType: 'blob',
        headers: getAuthHeaders(),
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          } else if (onProgress) {
            // Provide a minimal progress tick when total size is unavailable
            onProgress(10);
          }
        },
      }
    );

    // Extract filename from Content-Disposition header - fail loudly if missing
    const contentDisposition = response.headers['content-disposition'];
    if (!contentDisposition) {
      throw new Error('Server response missing Content-Disposition header. Solution needs to be re-uploaded.');
    }

    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (!filenameMatch || !filenameMatch[1]) {
      throw new Error(`Failed to extract filename from Content-Disposition header: ${contentDisposition}`);
    }

    const filename = filenameMatch[1];

    // Create blob and download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    // Mark as complete
    onProgress?.(100);
  } catch (error: any) {
    const errorMessage = error?.message || 'Failed to download solution file. The file may not be available.';
    alert(errorMessage);
    throw error;
  }
};

export const deleteSolution = async (problemId: string): Promise<void> => {
  try {
    // First get the solution to find the solution_id
    const checkResponse = await axios.get(
      `${API_BASE_URL}/solutions/${problemId}`,
      { headers: getAuthHeaders() }
    );

    if (!checkResponse.data.exists || !checkResponse.data.solution_id) {
      throw new Error('No solution found for this problem');
    }

    const solutionId = checkResponse.data.solution_id;

    // Delete using the existing submissions endpoint
    await axios.delete(
      `${API_BASE_URL}/solutions/submissions/${solutionId}`,
      { headers: getAuthHeaders() }
    );

    // Invalidate solution cache
    apiCache.invalidate(`/solutions/${problemId}`);
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Solution not found');
    }
    throw new Error(error?.message || 'Failed to delete solution');
  }
};

// Playground API functions
export const playgroundsApi = {
  // Get list of software that supports playgrounds
  getSoftwareList: async (): Promise<string[]> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/playgrounds/software-list`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching playground software list:", error);
      return [];
    }
  },
  
  // Create/get a playground instance for a specific software
  createPlayground: async (software: string): Promise<{ 
    access_url: string; 
    software: string; 
    user_id: string 
  } | null> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/playgrounds/`,
        { software },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating playground:", error);
      return null;
    }
  },
  
  // Delete/reset a playground instance
  deletePlayground: async (software: string): Promise<{ message: string } | null> => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/playgrounds/${software}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting playground:", error);
      return null;
    }
  }
};

// Playground Preview type for Mattermost playgrounds
export interface PlaygroundPreview {
  id: string;
  status: 'creating' | 'ready' | 'failed';
  access_url?: string;
  modal_sandbox_id?: string;
  playground_id?: string;
  software: string;
  user_id: string;
  message?: string;
  error?: string;
  started_at?: string;
  state_name?: string;
  state_type?: 'input' | 'output';
  resource_type?: string;
  resource_id?: string;
  ready_at?: string;
  last_accessed_at?: string;
}

// Create playground preview for a problem (loads Mattermost start state)
export const getPlaygroundPreviewStatus = async (
  problemId: string,
  software: string
): Promise<PlaygroundPreview> => {
  const response = await axios.post(
    `${API_BASE_URL}/playgrounds/previews/problem`,
    {
      problem_id: problemId,
      state_name: 'mattermost_start_state.zip',
      state_type: 'input',
      software: software
    },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Check health of playground preview
export const checkPlaygroundPreviewHealth = async (
  previewId: string
): Promise<{
  preview_id: string;
  software: string;
  status: 'healthy' | 'unhealthy' | 'failed';
  status_code?: number;
  response_time_ms?: number;
  error?: string;
}> => {
  const response = await axios.get(
    `${API_BASE_URL}/playgrounds/previews/${previewId}/health`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

// Delete playground preview
export const deletePlaygroundPreview = async (
  previewId: string
): Promise<{ message: string }> => {
  const response = await axios.delete(
    `${API_BASE_URL}/playgrounds/previews/${previewId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};
